// File: src/components/Cycling/PowerChart.js
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  Title,
} from 'chart.js';
import { useTranslation } from '../../i18n';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler, Title);

const PowerChart = ({ records = [] }) => {
  const { t } = useTranslation();

  const groupedData = useMemo(() => {
    if (!Array.isArray(records) || records.length === 0) return [];

    const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
    const format = (val, dec = 0) => (val !== null ? parseFloat(val.toFixed(dec)) : null);
    const buildPoint = (group, distance) => ({
      distance: parseFloat(distance.toFixed(2)),
      altitude: format(avg(group.map((e) => e.altitude ?? 0))),
      speed: format(avg(group.map((e) => (e.speed ?? 0) * 3.6)), 1),
      heart_rate: format(avg(group.map((e) => e.heart_rate ?? 0))),
      cadence: format(avg(group.map((e) => e.cadence ?? 0))),
      power: format(avg(group.map((e) => e.power ?? 0)), 1),
      temperature: format(avg(group.map((e) => e.temperature ?? 0))),
    });

    const out = [];
    let currentGroup = [];
    let currentStart = 0;

    records.forEach((r) => {
      const distanceKm = (r?.distance ?? 0) / 1000;
      if (distanceKm >= currentStart + 1) {
        if (currentGroup.length) out.push(buildPoint(currentGroup, currentStart + 1));
        currentStart += 1;
        currentGroup = [];
      }
      currentGroup.push(r);
    });

    if (currentGroup.length) {
      const lastDistance = (currentGroup.at(-1)?.distance ?? 0) / 1000;
      out.push(buildPoint(currentGroup, lastDistance));
    }

    return out;
  }, [records]);

  const chartData = useMemo(() => ({
    labels: groupedData.map((d) => d.distance),
    datasets: [
      {
        label: t('elevationProfile.seriesAltitudePlain'),
        data: groupedData.map((d) => d.altitude),
        borderColor: 'rgba(0,150,50,0.9)',
        backgroundColor: 'rgba(173,216,230,0.35)',
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2.5,
        yAxisID: 'alt',
        order: 5,
      },
      {
        label: t('elevationProfile.seriesSpeed'),
        data: groupedData.map((d) => d.speed),
        borderColor: 'rgba(59,130,246,0.9)',
        backgroundColor: 'rgba(59,130,246,0.05)',
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 1.5,
        yAxisID: 'right',
        order: 4,
      },
      {
        label: t('elevationProfile.seriesHr'),
        data: groupedData.map((d) => d.heart_rate),
        borderColor: 'rgba(239,68,68,0.85)',
        backgroundColor: 'rgba(239,68,68,0.05)',
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 1.5,
        yAxisID: 'right',
        order: 3,
      },
      {
        label: t('elevationProfile.seriesCadence'),
        data: groupedData.map((d) => d.cadence),
        borderColor: 'rgba(234,179,8,0.9)',
        backgroundColor: 'rgba(234,179,8,0.05)',
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 1.5,
        yAxisID: 'right',
        order: 2,
      },
      {
        label: t('elevationProfile.seriesPowerCycling'),
        data: groupedData.map((d) => d.power),
        borderColor: 'rgba(249,115,22,0.9)',
        backgroundColor: 'rgba(249,115,22,0.05)',
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        borderWidth: 1.2,
        yAxisID: 'right',
        order: 1,
      },
      {
        label: t('elevationProfile.seriesTemp'),
        data: groupedData.map((d) => d.temperature),
        borderColor: 'rgba(168,85,247,0.8)',
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 1,
        yAxisID: 'right',
        order: 0,
      },
    ],
  }), [groupedData, t]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { labels: { font: { size: 9 }, boxWidth: 10, padding: 6 } },
      tooltip: {
        callbacks: {
          label: (c) => {
            const units = ['m', 'KPH', 'bpm', 'RPM', 'W', '°C'];
            return ` ${c.dataset.label}: ${typeof c.raw === 'number' ? c.raw.toFixed(1) : c.raw} ${units[c.datasetIndex] || ''}`;
          },
        },
      },
    },
    scales: {
      x: { ticks: { font: { size: 8 } }, title: { display: true, text: t('elevationProfile.axisDistance'), font: { size: 9 } } },
      alt: { position: 'left', ticks: { font: { size: 8 }, stepSize: 50 }, title: { display: true, text: t('elevationProfile.axisAltitude'), font: { size: 8 } } },
      right: { position: 'right', ticks: { font: { size: 8 } }, grid: { drawOnChartArea: false }, min: 0, max: 250 },
    },
  }), [t]);

  if (!groupedData.length) return null;

  return <Line data={chartData} options={chartOptions} />;
};

export default PowerChart;
